import React, { useEffect, useState } from 'react';
import { X, MapPin, ShieldCheck, Send, UserCheck, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import API_URL from '@/config/api';
import { Button } from '@/components/ui/button';
import { formatApiError } from '@/utils/apiError';

const BRAND = { probound: ['ProBound', '#2F5E96'], proepoxy: ['ProEpoxy', '#7A0B7A'], prodecor: ['ProDecor', '#B8860B'] };

export function MatchInstallersModal({ lead, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [allSectors, setAllSectors] = useState(false);
  const [busy, setBusy] = useState('');

  const load = () => fetch(`${API_URL}/api/staff/leads/${lead.id}/matches?all_sectors=${allSectors}`, { credentials: 'include' })
    .then(async (r) => { const d = await r.json(); if (!r.ok) throw new Error(formatApiError(d)); setData(d); })
    .catch((e) => toast.error(e.message));
  useEffect(() => { load(); }, [lead.id, allSectors]); // eslint-disable-line react-hooks/exhaustive-deps

  const offer = async (m) => {
    setBusy(m.user_id);
    try {
      const r = await fetch(`${API_URL}/api/admin/leads/${lead.id}/assign`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ account_codes: [m.account_code], section: data.sector || lead.service_type }) });
      const d = await r.json();
      if (!r.ok) throw new Error(formatApiError(d) || 'Failed');
      if ((d.created || []).length) toast.success(`Offered to ${m.company_name || m.name} — they'll get a notification to accept`);
      else toast.message((d.skipped || [])[0]?.reason || 'Already offered');
      await load(); onChanged?.();
    } catch (e) { toast.error(e.message); } finally { setBusy(''); }
  };

  const assign = async (m) => {
    if (!window.confirm(`Put this lead straight into ${m.company_name || m.name}'s CRM?`)) return;
    setBusy(m.user_id);
    try {
      const r = await fetch(`${API_URL}/api/staff/leads/${lead.id}/assign-direct`, { method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ installer_id: m.user_id }) });
      const d = await r.json();
      if (!r.ok) throw new Error(formatApiError(d) || 'Failed');
      toast.success(`Assigned to ${d.installer_name}`);
      onChanged?.(); onClose();
    } catch (e) { toast.error(e.message); } finally { setBusy(''); }
  };

  const sectorLabel = data?.sector ? BRAND[data.sector][0] : 'any sector';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 sm:p-4" data-testid="match-modal">
      <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-2xl max-h-[92dvh] flex flex-col shadow-xl">
        <div className="flex items-start justify-between px-5 py-4 border-b">
          <div>
            <h2 className="font-bold text-gray-900 flex items-center gap-2"><MapPin className="w-5 h-5 text-[#006400]" /> Nearest installers</h2>
            <p className="text-xs text-gray-500 mt-0.5">{lead.customer_name} · {data?.postcode || lead.customer_postcode || lead.customer_address || 'no postcode'} · {sectorLabel}</p>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100" aria-label="Close" data-testid="match-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex-1 overflow-y-auto">
          {!data && <p className="p-6 text-sm text-gray-500 text-center">Finding installers…</p>}
          {data?.reason === 'no_postcode' && <p className="p-6 text-sm text-amber-800 bg-amber-50 m-4 rounded-lg flex gap-2" data-testid="match-no-postcode"><AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /> This lead has no UK postcode in its address — add one to the lead first, then try again.</p>}
          {data && data.matches?.length === 0 && data.reason !== 'no_postcode' && <p className="p-6 text-sm text-gray-500 text-center" data-testid="match-empty">No installers with a postcode on their profile{data.sector ? ` have ${BRAND[data.sector][0]} access` : ''}. {data.sector && <button className="underline text-[#006400]" onClick={() => setAllSectors(true)}>Show all sectors</button>}</p>}
          {data?.matches?.length > 0 && (
            <div className="divide-y" data-testid="match-list">
              {data.matches.map((m) => (
                <div key={m.user_id} className={`px-4 py-3 flex flex-wrap items-center gap-3 ${!m.within_radius ? 'opacity-60' : ''}`} data-testid={`match-row-${m.user_id}`}>
                  <div className="text-right w-14 shrink-0">
                    <div className="text-base font-bold text-gray-900">{m.distance_miles}</div>
                    <div className="text-[10px] uppercase text-gray-400 -mt-0.5">miles</div>
                  </div>
                  <div className="flex-1 min-w-[160px]">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-sm font-semibold text-gray-900">{m.company_name || m.name}</span>
                      {m.approved_for_lead && <span className="inline-flex items-center gap-0.5 text-[10px] font-bold bg-emerald-100 text-emerald-800 rounded px-1.5 py-0.5" data-testid={`match-approved-${m.user_id}`}><ShieldCheck className="w-3 h-3" /> Ox Approved</span>}
                      {m.priority && <span className="inline-flex items-center gap-0.5 text-[10px] font-bold bg-amber-100 text-amber-800 rounded px-1.5 py-0.5" data-testid={`match-priority-${m.user_id}`}>★ {m.plan} · priority</span>}
                      {m.is_assigned && <span className="text-[10px] font-bold bg-[#006400] text-white rounded px-1.5 py-0.5">Assigned</span>}
                      {m.offer_status && !m.is_assigned && <span className="text-[10px] font-bold bg-gray-100 text-gray-700 rounded px-1.5 py-0.5 capitalize">Offer {m.offer_status}</span>}
                    </div>
                    <div className="text-xs text-gray-500">{m.town || m.postcode} · {m.brands.map((b) => BRAND[b][0]).join(', ') || 'no sectors'} · travels {m.coverage_radius_miles ? `${m.coverage_radius_miles} mi` : '30 mi (default)'}{!m.within_radius ? ' — outside their radius' : ''}</div>
                  </div>
                  <div className="flex gap-1.5">
                    <Button size="sm" variant="outline" className="h-8 text-xs" disabled={!!busy || m.is_assigned || m.offer_status === 'pending'} onClick={() => offer(m)} data-testid={`match-offer-${m.user_id}`}><Send className="w-3.5 h-3.5 mr-1" /> Offer</Button>
                    <Button size="sm" className="h-8 text-xs bg-[#006400] hover:bg-[#004d00] text-white" disabled={!!busy || m.is_assigned} onClick={() => assign(m)} data-testid={`match-assign-${m.user_id}`}><UserCheck className="w-3.5 h-3.5 mr-1" /> Assign now</Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t flex items-center justify-between gap-2 text-xs text-gray-500">
          <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={allSectors} onChange={(e) => setAllSectors(e.target.checked)} data-testid="match-all-sectors" /> Include installers without {sectorLabel} access</label>
          <span><strong>Offer</strong> = they accept in the app · <strong>Assign now</strong> = straight into their CRM</span>
        </div>
      </div>
    </div>
  );
}
